import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { includes, isNil } from 'lodash-es';
import { TreeviewConfig } from './treeview-config';
import { TreeviewEventParser } from './treeview-event-parser';
import { TreeviewHeaderTemplateContext } from './treeview-header-template-context';
import { TreeviewHelper } from './treeview-helper';
import { TreeviewI18n } from './treeview-i18n';
import { TreeviewItem, TreeviewSelection } from './treeview-item';
import { TreeviewItemTemplateContext } from './treeview-item-template-context';
import { Datasource, IDatasource } from 'ngx-ui-scroll';

class FilterTreeviewItem extends TreeviewItem {
    private readonly refItem: TreeviewItem;
    constructor(item: TreeviewItem) {
        super({
            text: item.text,
            value: item.value,
            disabled: item.disabled,
            checked: item.checked,
            collapsed: item.collapsed,
            children: item.children
        });
        this.refItem = item;
    }

    updateParentChecked(checked) {

        this.refItem.checked = checked;
    }

    updateRefChecked() {
        this.children.forEach(child => {
            if (child instanceof FilterTreeviewItem) {
                child.updateRefChecked();
            }
        });

        let refChecked = this.checked;
        if (refChecked) {
            for (const refChild of this.refItem.children) {
                if (!refChild.checked) {
                    refChecked = false;
                    break;
                }
            }
        }
        this.refItem.checked = refChecked;
    }
}

@Component({
    selector: 'ngx-treeview',
    templateUrl: './treeview.component.html',
    styleUrls: ['./treeview.component.scss'],
    standalone: false
})
export class TreeviewComponent implements OnChanges {
    @Input() headerTemplate: TemplateRef<TreeviewHeaderTemplateContext>;
    @Input() itemTemplate: TemplateRef<TreeviewItemTemplateContext>;
    @Input() items: TreeviewItem[];
    @Input() config: TreeviewConfig;
    @Input() tab;
    @Input() totalLeavesCountOverride?: number;
    @Output() selectedChange = new EventEmitter<any[]>();
    @Output() allToggleChange = new EventEmitter<boolean>();
    @Output() filterChange = new EventEmitter<string>();
    @Output() clearAll = new EventEmitter<void>();
    headerTemplateContext: TreeviewHeaderTemplateContext;
    allItem: TreeviewItem;
    filterText = '';
    filterItems: TreeviewItem[];
    selection: TreeviewSelection;
    allCheckboxText: string;
    totalLeavesCount: number = 0;
    datasource: IDatasource = new Datasource({
        get: (index, count, success) => {
            const data: TreeviewItem[] = (this.filterItems || []).slice(index, index + count);
            success(data);
        },
        settings: {
            startIndex: 0,
        }
    });

    constructor(
        public i18n: TreeviewI18n,
        private defaultConfig: TreeviewConfig,
        private eventParser: TreeviewEventParser,
    ) {
        this.config = this.defaultConfig;
        this.allItem = new TreeviewItem({ text: 'All', value: undefined });
        this.allCheckboxText = this.i18n.getAllCheckboxText();
        this.createHeaderTemplateContext();
    }

    getAllStatus() {
        const hasDeselected = this.items?.some((categoryOrItem) =>
            categoryOrItem.children?.length ?
            categoryOrItem.children.filter((item) => !item.checked).length:
            !categoryOrItem.checked);
        const hasSelected = this.items?.some((categoryOrItem) =>
            categoryOrItem.children?.length ?
            categoryOrItem.children.filter((item) => item.checked).length:
            categoryOrItem.checked);
        return hasDeselected && !hasSelected ? false : hasDeselected ? undefined : true;
    }

    get hasFilterItems(): boolean {
        return !isNil(this.filterItems) && this.filterItems.length > 0;
    }

    get maxHeight(): string {
        return `${this.config.maxHeight}`;
    }

    ngOnChanges(changes: SimpleChanges) {
        const itemsSimpleChange = changes['items'];
        if (!isNil(itemsSimpleChange)) {
            if (!isNil(this.items)) {
                this.updateFilterItems();
                this.updateCollapsedOfAll();
                this.raiseSelectedChange();
                this.totalLeavesCount = typeof this.totalLeavesCountOverride === 'number'
                    ? this.totalLeavesCountOverride
                    : this.computeTotalLeavesCount(this.items);
            }
        }
        const countOverrideChange = changes['totalLeavesCountOverride'];
        if (!isNil(countOverrideChange)) {
            this.totalLeavesCount = typeof this.totalLeavesCountOverride === 'number'
                ? this.totalLeavesCountOverride
                : this.computeTotalLeavesCount(this.items);
        }
        this.createHeaderTemplateContext();
        this.allItem.checked = this.getAllStatus();
    }

    onLabelClick(item: TreeviewItem, onCheckedChange: () => void): void {
        const isParentToggleBlocked = this.config.isParentToggleDisabled && item.children && item.checked !== true;
        if (item.disabled || isParentToggleBlocked) {
            return;
        }
        item.checked = !item.checked;
        onCheckedChange();
    }

    onAllCollapseExpand() {
        this.allItem.collapsed = !this.allItem.collapsed;
        this.filterItems.forEach(item => item.setCollapsedRecursive(this.allItem.collapsed));
    }

    onFilterTextChange(text: string) {
        this.filterText = text;
        this.filterChange.emit(text);
        this.updateFilterItems();
        this.datasource.adapter.reload();
    }

    onAllCheckedChange() {
        const checked = this.allItem.checked;
        this.filterItems.forEach(item => {
            item.setCheckedRecursive(checked);
            if (item instanceof FilterTreeviewItem) {
                item.updateRefChecked();
            }
        });
        // Emit header toggle before raising selectedChange so listeners can coordinate
        this.allToggleChange.emit(checked);
        this.raiseSelectedChange();
    }

    clearAllItems(value) {
        this.filterText = '';
        this.updateFilterItems();
        this.onItemCheckedChange(value, false);
    }

    onItemCheckedChange(item: TreeviewItem, checked: boolean) {
        if (item instanceof FilterTreeviewItem) {
            if (this.config.decoupleChildFromParent) {
                item.updateParentChecked(checked);
            }
            else {
                item.updateRefChecked();
            }
        }


        this.updateCheckedOfAll();
        this.raiseSelectedChange();
    }

    raiseSelectedChange() {
        this.generateSelection();
        const values = this.eventParser.getSelectedChange(this);
        this.selectedChange.emit(values);
    }

    private createHeaderTemplateContext() {
        this.headerTemplateContext = {
            config: this.config,
            item: this.allItem,
            onCheckedChange: () => this.onAllCheckedChange(),
            onCollapseExpand: () => this.onAllCollapseExpand(),
            onFilterTextChange: (text) => this.onFilterTextChange(text)
        };
    }

    private generateSelection() {
        let checkedItems: TreeviewItem[] = [];
        let uncheckedItems: TreeviewItem[] = [];
        if (!isNil(this.items)) {
            const selection = TreeviewHelper.concatSelection(this.items, checkedItems, uncheckedItems);
            checkedItems = selection.checked;
            uncheckedItems = selection.unchecked;
        }

        this.selection = {
            checkedItems: checkedItems,
            uncheckedItems: uncheckedItems
        };
    }

    private updateFilterItems() {
        if (this.filterText !== '') {
            const filterItems: TreeviewItem[] = [];
            const filterText = this.filterText.toLowerCase();
            this.items.forEach(item => {
                const newItem = this.filterItem(item, filterText);
                if (!isNil(newItem)) {
                    filterItems.push(newItem);
                }
                item.correctChecked()
                if (item.checked === undefined) {
                    item.collapsed = false;
                }
            });
            this.filterItems = filterItems;
        } else {
            this.filterItems = this.items;
        }

        this.updateCheckedOfAll();
    }

    private computeTotalLeavesCount(items: TreeviewItem[]): number {
        let count = 0;
        const visit = (node: TreeviewItem) => {
            if (!node) return;
            const hasChildren = node.children && node.children.length > 0;
            if (!hasChildren && typeof node.value === 'number') {
                count++;
            } else if (hasChildren) {
                node.children.forEach(child => visit(child));
            }
        };
        (items || []).forEach(it => visit(it));
        return count;
    }

    private filterItem(item: TreeviewItem, filterText: string): TreeviewItem {
        const isMatch = includes(item.text.toLowerCase(), filterText);
        if (isMatch) {
            return item;
        } else {
            if (!isNil(item.children)) {
                const children: TreeviewItem[] = [];
                item.children.forEach(child => {
                    const newChild = this.filterItem(child, filterText);
                    if (!isNil(newChild)) {
                        children.push(newChild);
                    }
                });
                if (children.length > 0) {
                    const newItem = new FilterTreeviewItem(item);
                    newItem.collapsed = false;
                    newItem.children = children;
                    return newItem;
                }
            }
        }

        return undefined;
    }

    private updateCheckedOfAll() {
        let itemChecked: boolean = null;
        for (const filterItem of this.filterItems) {
            if (itemChecked === null) {
                itemChecked = filterItem.checked;
            } else if (itemChecked !== filterItem.checked) {
                itemChecked = undefined;
                break;
            }
        }

        if (itemChecked === null) {
            itemChecked = false;
        }

        this.allItem.checked = itemChecked;
    }
    selectAll(items) {
        const checkedItems = items.filter((item) => item.checked).map((item) => item.checked);
        if (checkedItems.length === items.length) {
            this.allItem.checked = true;
        }
    }

    private updateCollapsedOfAll() {
        let hasItemExpanded = false;
        for (const filterItem of this.filterItems) {
            if (!filterItem.collapsed) {
                hasItemExpanded = true;
                break;
            }
        }

        this.allItem.collapsed = !hasItemExpanded;
    }
    onClear() {
        this.onFilterTextChange('');
        if (this.config.decoupleChildFromParent) {
            this.items.forEach(child => {
                if (child.children) {
                    child.children.filter((c) => c.checked).map(() => child.collapsed = false);
                }
            });
        } else {
            this.items.forEach(child => {
                if(!child.children) {
                  return;
                }
                const checkedItems = child.children?.filter((c) => c.checked) || [];
                if (!child.checked) {
                    checkedItems.map(() => child.collapsed = false);
                }
                if(child.children.length == checkedItems.length) {
                    child.checked = true;
                } else {
                    if(checkedItems.length >= 1)
                        child.checked = undefined;
                    else
                        child.checked = false;
                }
            });
        }
        this.filterText = '';
        this.updateFilterItems();
    }

    clearTree() {
        this.clearAll.emit();
    }
}
